import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminFetchEmailLogs } from "../core/axios/endpoints";

export const EMAIL_STATUS_LABELS = {
  sent: { label: "Verstuurd", color: "bg-emerald-100 text-emerald-800" },
  failed: { label: "Mislukt", color: "bg-rose-100 text-rose-800" },
};

export const EMAIL_AUDIENCE_LABELS = {
  customer: { label: "Klant", color: "bg-sky-100 text-sky-800" },
  partner: { label: "Partner", color: "bg-violet-100 text-violet-800" },
  internal: { label: "Intern", color: "bg-gray-100 text-gray-700" },
};

// Leesbare namen voor `kind` (server: utils/mailer.ts log.kind per aanroep).
// Onbekende waarden vallen terug op de technische naam.
export const EMAIL_KIND_LABELS = {
  btw_bevestiging: "BTW bevestiging",
  btw_admin_notificatie: "Nieuwe BTW-aanvraag (intern)",
  btw_klantmail: "Klantmail (handmatig)",
  btw_deadline_herinnering: "Deadline-herinnering (intern)",
  subsidie_bevestiging: "Subsidie bevestiging",
  subsidie_admin_notificatie: "Nieuwe subsidie-aanvraag (intern)",
  subsidie_statuswijziging: "Statuswijziging subsidie",
  subsidie_klantmail: "Klantmail subsidie (handmatig)",
  factuur_aangemaakt: "Klantfactuur klaar",
  factuur_betaald: "Betaling ontvangen",
  partner_factuur: "Partner-factuur",
  partner_creditnota: "Partner-creditnota",
  formulier_contact: "Contactformulier",
  formulier_samenwerken: "Samenwerken-formulier",
  formulier_btw_escalatie: "BTW-test escalatie",
  account_profiel_afronden: "Account: profiel afronden",
  account_wachtwoord_reset: "Account: wachtwoord-reset",
  onbekend: "Overig",
};

// Herkomst van een regel (server: EmailLogs.origin). Live = op het moment van
// verzenden gelogd. Geïmporteerd = achteraf uit Resend gehaald, ook de echte
// mail. Nagebouwd = uit eigen sporen gereconstrueerd; kan afwijken.
export const EMAIL_ORIGIN_LABELS = {
  live: { label: "Live", color: "" },
  resend_import: { label: "Geïmporteerd", color: "bg-slate-100 text-slate-700" },
  reconstructed: { label: "Nagebouwd", color: "bg-amber-100 text-amber-800" },
};

// Bezorgstatus volgens Resend (deliveryStatus), los van onze verzendstatus.
export const EMAIL_DELIVERY_LABELS = {
  delivered: { label: "Bezorgd", color: "bg-emerald-50 text-emerald-700" },
  opened: { label: "Geopend", color: "bg-emerald-50 text-emerald-700" },
  clicked: { label: "Geklikt", color: "bg-emerald-50 text-emerald-700" },
  sent: { label: "Verzonden", color: "bg-gray-100 text-gray-600" },
  delivery_delayed: { label: "Vertraagd", color: "bg-amber-100 text-amber-800" },
  bounced: { label: "Gebounced", color: "bg-rose-100 text-rose-800" },
  complained: { label: "Spam-melding", color: "bg-rose-100 text-rose-800" },
  failed: { label: "Niet bezorgd", color: "bg-rose-100 text-rose-800" },
  suppressed: { label: "Onderdrukt", color: "bg-rose-100 text-rose-800" },
};

export const emailKindLabel = (kind) => EMAIL_KIND_LABELS[kind] ?? kind ?? "-";

// Kleine badge voor herkomst (alleen als niet live) en bezorgstatus (alleen
// als bekend en niet gewoon bezorgd/verzonden).
export const OriginBadges = ({ row }) => {
  const origin = row?.origin && row.origin !== "live" ? EMAIL_ORIGIN_LABELS[row.origin] : null;
  const delivery =
    row?.deliveryStatus && !["delivered", "sent", "opened", "clicked"].includes(row.deliveryStatus)
      ? EMAIL_DELIVERY_LABELS[row.deliveryStatus] ?? { label: row.deliveryStatus, color: "bg-gray-100 text-gray-600" }
      : null;
  if (!origin && !delivery) return null;
  return (
    <>
      {origin ? (
        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${origin.color}`}>
          {origin.label}
        </span>
      ) : null}
      {delivery ? (
        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${delivery.color}`}>
          {delivery.label}
        </span>
      ) : null}
    </>
  );
};

export const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const Badge = ({ map, value }) => {
  const cfg = map[value] ?? { label: value ?? "-", color: "bg-gray-100 text-gray-700" };
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

// Link naar het dossier waar de mail bij hoort (alleen in het algemene overzicht).
export const applicationLink = (row) => {
  if (!row?.applicationId) return null;
  if (row.applicationModel === "BtwApplications") {
    return `/panel/admin/btw-batterij-overzicht/${row.applicationId}`;
  }
  if (row.applicationModel === "Applications") {
    // De subsidie-detailroute is per regeling; EIA-pad werkt voor elke
    // aanvraag omdat de pagina op id laadt.
    return `/panel/admin/eia-overzicht/aanvraag-details/${row.applicationId}`;
  }
  return null;
};

/**
 * Tabel met verstuurde mails. Twee gebruiken:
 *  - compact (detailpagina): alleen `applicationId` + `applicationModel`
 *  - volledig (pagina "Verstuurde mails"): `filters` van buiten, eigen paginatie
 * Klik op een rij opent de preview via `onOpen(id)`.
 */
const EmailLogList = ({
  applicationId = "",
  applicationModel = "",
  filters = {},
  compact = false,
  limit = 25,
  reloadKey = 0,
  onOpen,
}) => {
  const [data, setData] = useState({ docs: [], pagination: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const filterKey = JSON.stringify(filters);
  const scopeKey = `${filterKey}|${applicationId}`;
  const lastScopeRef = useRef(scopeKey);

  useEffect(() => {
    // Nieuw filter of ander dossier: eerst terug naar pagina 1, en pas dan
    // laden. Anders gaat er een request uit met de oude pagina en kan een
    // laat antwoord het nieuwe resultaat overschrijven.
    if (lastScopeRef.current !== scopeKey) {
      lastScopeRef.current = scopeKey;
      if (page !== 1) {
        setPage(1);
        return undefined;
      }
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminFetchEmailLogs({
      page,
      limit,
      applicationId,
      applicationModel,
      ...JSON.parse(filterKey),
    })
      .then((body) => {
        if (cancelled) return;
        const payload = body?.data ?? body;
        setData({
          docs: payload?.docs ?? [],
          pagination: payload?.pagination ?? {},
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error ?? "Mails laden mislukt.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeKey, filterKey, page, limit, applicationId, applicationModel, reloadKey]);

  const cols = compact ? 4 : 6;

  return (
    <div>
      {error ? (
        <div role="alert" className="mb-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
          {error}
        </div>
      ) : null}
      <div className={`overflow-hidden ${compact ? "rounded-lg ring-1 ring-gray-100" : "rounded-xl bg-white shadow-sm ring-1 ring-gray-200"}`}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-3 py-2">Datum</th>
              {compact ? null : <th className="px-3 py-2">Aan</th>}
              <th className="px-3 py-2">Onderwerp</th>
              <th className="px-3 py-2">Soort</th>
              {compact ? null : <th className="px-3 py-2">Dossier</th>}
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && data.docs.length === 0 ? (
              <tr>
                <td colSpan={cols} className="px-3 py-8 text-center text-gray-500">
                  Laden…
                </td>
              </tr>
            ) : data.docs.length === 0 ? (
              <tr>
                <td colSpan={cols} className="px-3 py-8 text-center text-gray-500">
                  {compact
                    ? "Nog geen mails verstuurd voor dit dossier."
                    : "Geen mails gevonden."}
                </td>
              </tr>
            ) : (
              data.docs.map((row) => {
                const link = compact ? null : applicationLink(row);
                return (
                  <tr
                    key={row._id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => onOpen?.(row._id)}
                  >
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-700">
                      {fmtDateTime(row.sentAt ?? row.createdAt)}
                    </td>
                    {compact ? null : (
                      <td className="max-w-[220px] px-3 py-2">
                        {/* Bij interne mails is de ontvanger ons eigen adres;
                            `recipientName` is dan de klant waar het over gaat. */}
                        {row.audience === "internal" ? (
                          <>
                            <div className="truncate font-medium text-gray-900">{row.to}</div>
                            {row.recipientName ? (
                              <div className="truncate text-xs text-gray-500">
                                betreft {row.recipientName}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <div className="truncate font-medium text-gray-900">
                              {row.recipientName || row.to}
                            </div>
                            {row.recipientName ? (
                              <div className="truncate text-xs text-gray-500">{row.to}</div>
                            ) : null}
                          </>
                        )}
                      </td>
                    )}
                    <td className="max-w-[320px] px-3 py-2">
                      <div className="truncate text-gray-900">{row.subject}</div>
                      {compact && row.to ? (
                        <div className="truncate text-xs text-gray-500">
                          naar {row.to}
                          {row.actor?.naam ? ` · door ${row.actor.naam}` : ""}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge map={EMAIL_AUDIENCE_LABELS} value={row.audience} />
                        <span className="text-xs text-gray-600">{emailKindLabel(row.kind)}</span>
                      </div>
                    </td>
                    {compact ? null : (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        {link ? (
                          <Link to={link} className="text-xs text-emerald-700 hover:underline">
                            {row.applicationModel === "BtwApplications" ? "BTW-aanvraag" : "Subsidie-aanvraag"}
                          </Link>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge map={EMAIL_STATUS_LABELS} value={row.status} />
                        <OriginBadges row={row} />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {data.pagination?.totalPages > 1 ? (
        <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
          <span>
            Pagina {data.pagination.page} van {data.pagination.totalPages}
            {data.pagination.totalDocs ? ` · ${data.pagination.totalDocs} mails` : ""}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 disabled:opacity-50"
            >
              Vorige
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= (data.pagination.totalPages ?? 1) || loading}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 disabled:opacity-50"
            >
              Volgende
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EmailLogList;
